import "../Styles/TipTap.scss";
import "../Styles/Mention.scss";
import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Placeholder from "@tiptap/extension-placeholder";
import { useEffect, useState } from "react";
import "tippy.js/dist/tippy.css"; // Optional: tippy CSS for styling tooltips
import useAppStore from "../../Store/SearchStore.jsx";
import usePersonaStore from "../../Store/PersonaStore.jsx";

import AutocompleteExtension from "./AutoCompleteExtension.jsx";
// import { AutocompleteExtension } from "./AutoCompleteExtensionChatGPT.jsx";
import { CustomMentionNode } from "./nodes/CustomMentionNode";
import { PersonaSummaryNode } from "./nodes/PersonaSummaryNode";
import { convertCurlyBracesToMentionTags } from "../../Utils/personaUtils.js";

const MyEditorComponent = ({
  searchText,
  setSearchText,
  placeholders,
  handleSearchButtonClick,
}) => {
  const { tag, mounted, setMounted, isVoiceRecognitionActive } = useAppStore();
  const { selectedPersona } = usePersonaStore();

  const [placeholderIndex, setPlaceholderIndex] = useState(0);
  const [isEditorFocused, setIsEditorFocused] = useState(false);

  // Check if we're showing persona summary as placeholder
  const isShowingPersonaSummary = () => {
    return selectedPersona && selectedPersona.summary && (!searchText || searchText.trim() === "" || searchText === "<p></p>");
  };

  // Get current placeholder based on persona selection
  const getCurrentPlaceholder = () => {
    // If persona is selected and has summary, and search text is empty, show summary as placeholder
    if (isShowingPersonaSummary()) {
      return selectedPersona.summary;
    }

    return placeholders[placeholderIndex];
  };

  /**
   * Handles the transformation of content, converting curly brace mentions to HTML spans
   * - Triple curly braces {{{text}}} are converted to edited mentions
   * - Single curly braces {text} are converted to regular mentions
   * @param {string} content - The content to transform
   */
  const handleProcedureContentChange = (content) => {
    const formattedContent = convertCurlyBracesToMentionTags(content);
    setSearchText(formattedContent);
  };

  // Rotate placeholders when editor is not focused and no persona is selected
  useEffect(() => {
    if (!isEditorFocused && (!selectedPersona || !selectedPersona.summary)) {
      const intervalId = setInterval(() => {
        setPlaceholderIndex(
          (prevIndex) => (prevIndex + 1) % placeholders.length
        );
      }, 2000); // Rotate every 5 seconds

      return () => clearInterval(intervalId);
    }
  }, [isEditorFocused, placeholders, selectedPersona]);

  // Initialize the editor with extensions and content
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        keyboardShortcuts: {
          Enter: false, // Disable default "Enter" behavior
        },
      }),
      AutocompleteExtension.configure({ char: "@" }),
      AutocompleteExtension,
      Placeholder.configure({
        placeholder: getCurrentPlaceholder(),
      }),
      CustomMentionNode,
      PersonaSummaryNode,
    ],
    content: searchText || "",
    onUpdate: ({ editor }) => {
      const content = editor.getHTML();
      if (content !== searchText) {
        setSearchText(content);
        localStorage.setItem("searchText", content);
        handleProcedureContentChange(content);
      }
    },
    onFocus: () => setIsEditorFocused(true),
    onBlur: () => setIsEditorFocused(false),
    editorProps: {
      handleKeyDown: (() => {
        let enterPressTimeout = null;
        let enterPressCount = 0;

        return (view, event) => {
          if (event.key === "Enter" && !event.shiftKey) {
            event.preventDefault(); // Prevent default behavior

            enterPressCount++;

            if (enterPressCount === 1) {
              // Trigger the search function on the first Enter
              handleSearchButtonClick();

              // Reset after a short delay
              enterPressTimeout = setTimeout(() => {
                enterPressCount = 0;
              }, 300); // Adjust delay for double Enter (300ms recommended)
            }

            if (enterPressCount === 2) {
              view.dispatch(view.state.tr.insertText("\n"));
              enterPressCount = 0; // Reset counter
              clearTimeout(enterPressTimeout);
            }

            return true; // Indicate the event was handled
          }

          return false; // Let other keys behave normally
        };
      })(),
    },
  });

  // Update editor content when searchText changes
  useEffect(() => {
    if (editor && searchText !== editor.getHTML()) {
      // Convert curly braces to HTML mention tags if needed
      // This handles the case where searchText comes from localStorage with curly braces
      let contentToSet = searchText;
      if (contentToSet && (contentToSet.includes('{') || contentToSet.includes('}'))) {
        contentToSet = convertCurlyBracesToMentionTags(contentToSet);
      }

      // Check if the incoming searchText contains a persona summary
      const searchTextHasPersonaSummary = contentToSet.includes('data-type="persona-summary"');

      // Set the new content
      editor.commands.setContent(contentToSet);

      // If searchText has a persona summary, trigger the typing animation
      // BUT skip animation if voice recognition is active to prevent constant retriggering
      if (searchTextHasPersonaSummary && !isVoiceRecognitionActive) {
        requestAnimationFrame(() => {
          const summaryElements = document.querySelectorAll(".persona-summary-text");
          const lastSummary = summaryElements[summaryElements.length - 1];
          if (lastSummary && selectedPersona && selectedPersona.summary) {
            // Create a temporary element to measure the width
            const tempSpan = document.createElement("span");
            tempSpan.style.padding = "0.2em 0.4em";
            tempSpan.style.fontSize = "inherit";
            tempSpan.style.fontFamily = "inherit";
            tempSpan.style.lineHeight = "inherit";
            tempSpan.style.visibility = "hidden";
            tempSpan.style.position = "absolute";
            tempSpan.style.whiteSpace = "nowrap";
            tempSpan.style.boxSizing = "border-box";
            tempSpan.textContent = selectedPersona.summary;
            document.body.appendChild(tempSpan);

            // Get the exact width of the text content
            const textWidth = tempSpan.scrollWidth;
            document.body.removeChild(tempSpan);

            // Set initial state for animation
            lastSummary.style.width = "0";
            lastSummary.style.overflow = "hidden";
            lastSummary.style.display = "inline-block";
            lastSummary.style.verticalAlign = "middle";
            lastSummary.style.opacity = "1";
            lastSummary.style.padding = "0.2em 0.4em";
            lastSummary.style.boxSizing = "border-box";

            // Typing effect: faster typing animation
            const msPerChar = 20; // milliseconds per character
            const totalDuration = selectedPersona.summary.length * msPerChar;
            const frameRate = 16; // ~60fps for smooth animation
            const totalFrames = totalDuration / frameRate;
            let currentWidth = 0;
            let frame = 0;

            const interval = setInterval(() => {
              frame++;
              currentWidth = (frame / totalFrames) * textWidth;

              if (frame >= totalFrames) {
                clearInterval(interval);
                lastSummary.style.width = `${textWidth}px`;
              } else {
                lastSummary.style.width = `${currentWidth}px`;
              }
            }, frameRate);
          }
        });
      }
    }
  }, [searchText, editor, selectedPersona]);

  // Update placeholder when searchText becomes empty or when persona changes
  useEffect(() => {
    if (editor) {
      const placeholderExtension = editor.extensionManager.extensions.find(
        (ext) => ext.name === "placeholder"
      );
      if (placeholderExtension) {
        const newPlaceholder = getCurrentPlaceholder();
        placeholderExtension.options.placeholder = newPlaceholder;
        // Force a re-render of the placeholder
        editor.view.dispatch(editor.state.tr.setMeta("placeholder", { placeholder: newPlaceholder }));
      }
    }
  }, [searchText, selectedPersona]);

  // Update placeholder configuration when placeholderIndex changes (for rotation)
  useEffect(() => {
    if (editor && (!selectedPersona || !selectedPersona.summary)) {
      const placeholderExtension = editor.extensionManager.extensions.find(
        (ext) => ext.name === "placeholder"
      );
      if (placeholderExtension) {
        const newPlaceholder = getCurrentPlaceholder();
        placeholderExtension.options.placeholder = newPlaceholder;
        editor.view.dispatch(editor.state.tr.setMeta("placeholder", { placeholder: newPlaceholder }));
      }
    }
  }, [placeholderIndex, editor]);

  // Handle inserting a mention when tag changes
  useEffect(() => {
    if (editor && tag && tag !== "" && tag !== null && tag !== undefined) {
      handleInsertMention();
    }
  }, [tag, editor]);

  // Now persona summaries are only inserted via searchText (single source of truth)
  // This prevents duplicate insertions and simplifies the logic

  // Function to insert a mention into the editor
  const handleInsertMention = () => {
    if (!editor || !tag || tag === "" || tag === null || tag === undefined) {
      console.warn("Cannot insert mention: invalid tag value", tag);
      return;
    }

    // Trigger search immediately
    handleSearchButtonClick();

    // First, create a temporary element to measure the width
    const tempSpan = document.createElement("span");
    tempSpan.style.padding = "0.2em 0.4em";
    tempSpan.style.fontSize = "inherit";
    tempSpan.style.fontFamily = "inherit";
    tempSpan.style.lineHeight = "inherit";
    tempSpan.style.visibility = "hidden";
    tempSpan.style.position = "absolute";
    tempSpan.style.whiteSpace = "nowrap";
    tempSpan.style.boxSizing = "border-box";
    tempSpan.textContent = tag;
    document.body.appendChild(tempSpan);

    // Get the exact width of the text content
    const textWidth = tempSpan.scrollWidth;
    document.body.removeChild(tempSpan);

    // Insert the mention with initial hidden state
    editor
      .chain()
      .focus()
      .insertContent(" ")
      .insertContent({
        type: "mention",
        attrs: {
          id: 1,
          label: tag,
        },
      })
      .insertContent(" ")
      .run();

    // Start the typing animation
    requestAnimationFrame(() => {
      const mentionElements = document.querySelectorAll(".mention-text");
      const lastMention = mentionElements[mentionElements.length - 1];
      if (lastMention) {
        // Set initial state
        lastMention.style.width = "0";
        lastMention.style.overflow = "hidden";
        lastMention.style.display = "inline-block";
        lastMention.style.verticalAlign = "middle";
        lastMention.style.opacity = "1";
        lastMention.style.padding = "0.2em 0.4em";
        lastMention.style.boxSizing = "border-box";

        const duration = 1000; // 1 second
        const steps = tag.length;
        const stepDuration = duration / steps;
        let currentWidth = 0;

        const interval = setInterval(() => {
          currentWidth += textWidth / steps;
          if (currentWidth >= textWidth) {
            clearInterval(interval);
            lastMention.style.width = `${textWidth}px`;
          } else {
            lastMention.style.width = `${currentWidth}px`;
          }
        }, stepDuration);
      }
    });

    setMounted(true);
  };

  // Trigger search when mounted state changes (after animation completes)
  useEffect(() => {
    if (mounted) {
      handleSearchButtonClick();
      setMounted(false);
    }
  }, [mounted]);

  // Focus editor and handle key events, as well as allow enter button to trigger search
  useEffect(() => {
    const handleKeyDown = (event) => {
      // Handle "Ctrl+A" or "Command+A" for select all
      if ((event.ctrlKey || event.metaKey) && event.key === "a") {
        // Only hijack if the editor is focused
        if (!editor || !editor.isFocused) return;

        event.preventDefault(); // Prevent the default behavior

        const { state } = editor;
        const { doc } = state;

        // Check if document starts with persona summary
        const firstNode = doc.firstChild;
        if (firstNode && firstNode.type.name === "personaSummary") {
          // Select everything AFTER the persona summary
          const personaSummarySize = firstNode.nodeSize;
          const tr = state.tr.setSelection(
            state.selection.constructor.create(
              doc,
              personaSummarySize,
              doc.content.size
            )
          );
          editor.view.dispatch(tr);
        } else {
          // No persona summary, select all normally
          editor.commands.selectAll();
        }
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [editor, handleSearchButtonClick]);

  // Render the editor component
  return (
    <div className={`editor-wrapper ${isShowingPersonaSummary() ? 'persona-placeholder' : ''}`}>
      <button
        className="menubar__button"
        onClick={handleInsertMention}
        style={{ display: "none" }}
      >
        <span>Insert Mention</span>
      </button>
      <EditorContent className="h-full" editor={editor} />
    </div>
  );
};

export default MyEditorComponent;
